import { SEO } from "../components/SEO";
import { PageHero } from "../components/PageHero";
import { Section, SectionHeader } from "../components/Section";
import { FeatureCard } from "../components/Card";
import { CTASection } from "../components/CTASection";
import { useTranslation } from "../lib/i18n";

export default function ArchitecturePage() {
  const { t } = useTranslation();

  const LAYERS = [
    {
      title: t("architecture.layer1.title"),
      description: t("architecture.layer1.desc"),
      points: [
        t("architecture.layer1.p1"),
        t("architecture.layer1.p2"),
        t("architecture.layer1.p3"),
        t("architecture.layer1.p4"),
      ],
    },
    {
      title: t("architecture.layer2.title"),
      description: t("architecture.layer2.desc"),
      points: [
        t("architecture.layer2.p1"),
        t("architecture.layer2.p2"),
        t("architecture.layer2.p3"),
        t("architecture.layer2.p4"),
      ],
    },
    {
      title: t("architecture.layer3.title"),
      description: t("architecture.layer3.desc"),
      points: [
        t("architecture.layer3.p1"),
        t("architecture.layer3.p2"),
        t("architecture.layer3.p3"),
        t("architecture.layer3.p4"),
      ],
    },
    {
      title: t("architecture.layer4.title"),
      description: t("architecture.layer4.desc"),
      points: [
        t("architecture.layer4.p1"),
        t("architecture.layer4.p2"),
        t("architecture.layer4.p3"),
        t("architecture.layer4.p4"),
      ],
    },
  ];

  return (
    <>
      <SEO
        title="Architecture — Sentinel"
        description="Sentinel operates through a four-layer architecture: Sensor Fusion, Diagnostic Intelligence, Autonomous Intervention, and Sentinel Command."
        canonical="/architecture"
        image="https://images.pexels.com/photos/5480781/pexels-photo-5480781.jpeg?auto=compress&cs=tinysrgb&w=1200"
      />
      <PageHero
        eyebrow={t("architecture.page.eyebrow")}
        title={t("architecture.page.title")}
        description={t("architecture.page.description")}
        bgVariant="slate"
        bgImage="https://images.pexels.com/photos/5480781/pexels-photo-5480781.jpeg?auto=compress&cs=tinysrgb&w=1600"
      />

      <Section>
        <SectionHeader
          eyebrow={t("architecture.layers.eyebrow")}
          title={t("architecture.layers.title")}
        />
        {/*
          Layout canvas: 1200 × 960
          Logo center: (600, 480)  Logo size: 260px
          Logo corner arm-tips (≈45° at ~72% of half-radius from center):
            top-left:     (507, 387)
            top-right:    (693, 387)
            bottom-left:  (507, 573)
            bottom-right: (693, 573)
          Card inner corners (card width=300, card heights ~380):
            top-left card     bottom-right: (300, 380)
            top-right card    bottom-left:  (900, 380)
            bottom-left card  top-right:    (300, 580)
            bottom-right card top-left:     (900, 580)
        */}
        <div className="layer-diagram-desktop">
          <div
            style={{
              position: "relative",
              width: "100%",
              maxWidth: "1200px",
              margin: "0 auto",
              height: "960px",
            }}
          >
            {/* Connector lines + animated beads */}
            <svg
              style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none", zIndex: 1 }}
              viewBox="0 0 1200 960"
              preserveAspectRatio="none"
            >
              {[
                { x1: 300, y1: 380, x2: 507, y2: 387, delay: 0 },
                { x1: 900, y1: 380, x2: 693, y2: 387, delay: 0.6 },
                { x1: 300, y1: 580, x2: 507, y2: 573, delay: 1.2 },
                { x1: 900, y1: 580, x2: 693, y2: 573, delay: 1.8 },
              ].map((ln, i) => (
                <g key={`conn-${i}`}>
                  <line x1={ln.x1} y1={ln.y1} x2={ln.x2} y2={ln.y2} stroke="var(--accent-500)" strokeWidth="2.5" strokeDasharray="7 5" opacity="0.65" />
                  <circle r="5" fill="var(--accent-300)" opacity="0.9">
                    <animate attributeName="cx" values={`${ln.x2};${ln.x1};${ln.x2}`} dur="3s" begin={`${ln.delay}s`} repeatCount="indefinite" />
                    <animate attributeName="cy" values={`${ln.y2};${ln.y1};${ln.y2}`} dur="3s" begin={`${ln.delay}s`} repeatCount="indefinite" />
                    <animate attributeName="r" values="5;7;5" dur="3s" begin={`${ln.delay}s`} repeatCount="indefinite" />
                    <animate attributeName="opacity" values="0.4;1;0.4" dur="3s" begin={`${ln.delay}s`} repeatCount="indefinite" />
                  </circle>
                </g>
              ))}
              {/* Dot at each logo arm tip */}
              {[[507,387],[693,387],[507,573],[693,573]].map(([cx,cy]) => (
                <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r="5" fill="var(--accent-500)" opacity="0.8" />
              ))}
            </svg>

            {/* Center logo */}
            <div
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                zIndex: 2,
              }}
            >
              <img
                src="/sentinel_logo.png"
                alt="Sentinel logo"
                style={{ width: "260px", height: "260px", objectFit: "contain" }}
              />
            </div>

            {/* Corner cards */}
            {LAYERS.map((layer, i) => {
              const positions: React.CSSProperties[] = [
                { top: 0, left: 0 },
                { top: 0, right: 0 },
                { bottom: 0, left: 0 },
                { bottom: 0, right: 0 },
              ];
              const aligns: React.CSSProperties[] = [
                { textAlign: "left" },
                { textAlign: "right" },
                { textAlign: "left" },
                { textAlign: "right" },
              ];
              return (
                <div
                  key={layer.title}
                  style={{
                    position: "absolute",
                    ...positions[i],
                    width: "300px",
                    zIndex: 3,
                  }}
                >
                  <div
                    className="card"
                    style={{ padding: "1.5rem", borderRadius: "var(--radius-lg)", ...aligns[i] }}
                  >
                    <div
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "38px",
                        height: "38px",
                        borderRadius: "var(--radius-md)",
                        background: "var(--accent-500)",
                        color: "#fff",
                        fontWeight: 800,
                        fontSize: "1rem",
                        marginBottom: "0.875rem",
                      }}
                    >
                      {i + 1}
                    </div>
                    <h3 style={{ fontSize: "1.35rem", marginBottom: "0.75rem", lineHeight: 1.25, fontWeight: 700 }}>{layer.title}</h3>
                    <p className="text-muted" style={{ fontSize: "0.95rem", marginBottom: "1rem", lineHeight: 1.6 }}>
                      {layer.description}
                    </p>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem", justifyContent: i % 2 === 1 ? "flex-end" : "flex-start" }}>
                      {layer.points.map((p) => (
                        <span key={p} className="badge badge-accent" style={{ fontSize: "0.8125rem", padding: "0.375rem 0.75rem" }}>{p}</span>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Mobile stacked layout */}
        <div className="layer-diagram-mobile">
          <div style={{ display: "flex", flexDirection: "column", gap: "1.5rem", maxWidth: "420px", margin: "0 auto" }}>
            {LAYERS.map((layer, i) => (
              <div key={layer.title} className="card" style={{ padding: "1.25rem", borderRadius: "var(--radius-lg)" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "0.75rem" }}>
                  <div
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "34px",
                      height: "34px",
                      borderRadius: "var(--radius-md)",
                      background: "var(--accent-500)",
                      color: "#fff",
                      fontWeight: 800,
                      fontSize: "0.9rem",
                      flexShrink: 0,
                    }}
                  >
                    {i + 1}
                  </div>
                  <h3 style={{ fontSize: "1.2rem", fontWeight: 700, margin: 0 }}>{layer.title}</h3>
                </div>
                <p className="text-muted" style={{ fontSize: "0.9rem", marginBottom: "0.75rem", lineHeight: 1.55 }}>
                  {layer.description}
                </p>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem" }}>
                  {layer.points.map((p) => (
                    <span key={p} className="badge badge-accent" style={{ fontSize: "0.75rem", padding: "0.3rem 0.6rem" }}>{p}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
        <style>{`
          .layer-diagram-mobile { display: none; }
          @media (max-width: 900px) {
            .layer-diagram-desktop { display: none; }
            .layer-diagram-mobile { display: block; }
          }
        `}</style>
      </Section>

      <Section variant="subtle">
        <SectionHeader eyebrow={t("architecture.flow.eyebrow")} title={t("architecture.flow.title")} />
        <DataFlowDiagram />
      </Section>

      <CTASection
        title={t("architecture.cta.title")}
        description={t("architecture.cta.description")}
        primaryLabel={t("cta.downloadArchitecture")}
        primaryTo="/downloads"
        secondaryLabel={t("cta.exploreDomains")}
        secondaryTo="/domains"
      />
    </>
  );
}

function DataFlowDiagram() {
  const { t } = useTranslation();
  const boxes = [
    { x: 115, label: t("architecture.flow.box1"), sub: "Layer 1" },
    { x: 355, label: t("architecture.flow.box2"), sub: "Layer 2" },
    { x: 635, label: t("architecture.flow.box3"), sub: "Layer 3" },
    { x: 875, label: t("architecture.flow.box4"), sub: "Layer 4" },
  ];
  const w = 210;
  const cy = 170;

  const segments = [
    { x1: 0, x2: 115, delay: 0 },
    { x1: 325, x2: 355, delay: 0.8 },
    { x1: 565, x2: 635, delay: 1.6 },
    { x1: 845, x2: 875, delay: 2.4 },
    { x1: 1085, x2: 1200, delay: 3.2 },
  ];

  return (
    <div style={{ width: "100%", overflowX: "auto" }}>
      <svg viewBox="0 0 1200 340" style={{ width: "100%", minWidth: "760px", height: "auto" }}>
        {segments.map((s, i) => (
          <line
            key={`line-${i}`}
            x1={s.x1}
            y1={cy}
            x2={s.x2}
            y2={cy}
            stroke="var(--accent-500)"
            strokeWidth={2.5}
            strokeDasharray="10 6"
            opacity={0.55}
            style={{ animation: "flowDash 1s linear infinite" }}
          />
        ))}

        {segments.map((s, i) => (
          <circle key={`pulse-${i}`} r={6} fill="var(--accent-300)">
            <animate
              attributeName="cx"
              values={`${s.x1};${s.x2};${s.x2}`}
              dur="4s"
              begin={`${s.delay}s`}
              repeatCount="indefinite"
            />
            <animate
              attributeName="cy"
              values={`${cy};${cy};${cy}`}
              dur="4s"
              begin={`${s.delay}s`}
              repeatCount="indefinite"
            />
            <animate
              attributeName="opacity"
              values="0;1;1;0;0"
              dur="4s"
              begin={`${s.delay}s`}
              repeatCount="indefinite"
            />
          </circle>
        ))}

        {boxes.map((b, i) => {
          const cx = b.x + w / 2;
          return (
            <g key={`box-${i}`}>
              <rect
                x={b.x}
                y={cy - 60}
                width={w}
                height={120}
                rx={12}
                fill="var(--bg-dark-slate)"
                stroke="var(--accent-500)"
                strokeWidth={1.5}
              />
              <circle
                cx={cx}
                cy={cy}
                r={5}
                fill="var(--accent-400)"
                style={{ animation: "nodeGlow 2s ease-in-out infinite", animationDelay: `${i * 0.4}s` }}
              />
              <text x={cx} y={cy - 18} textAnchor="middle" fill="#e2e8f0" fontSize="15" fontWeight="700">
                {b.label}
              </text>
              <text x={cx} y={cy + 24} textAnchor="middle" fill="var(--accent-400)" fontSize="11" fontWeight="600" letterSpacing="1">
                {b.sub.toUpperCase()}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}
